import type { SeatDetail, SeatingAvailabilitySummary } from '@gatecheckpro/contracts';
import { SEAT_AVAILABILITY_STATES, presentation } from '@gatecheckpro/design-system';
import type { OverlayAppearance } from './VenueMapCanvas.tsx';

/**
 * The server's answer about the chairs a person is looking at.
 *
 * Two rules govern this module and neither is negotiable.
 *
 * **Nothing here computes availability.** The server sends it already derived
 * from a commercial disposition and a live claim state, and the browser looks
 * it up. Deriving it here — even obviously, even by comparing a hold list
 * against a seat list — would create a second answer to a question that has
 * one, and the second answer would be wrong every time a page sat open while a
 * room filled.
 *
 * **The index is keyed on the exact occurrence.** `venueMapSeatId` identifies a
 * chair *in this map version*, which is what the configuration pinned and what
 * the detail query is asked for. Durable identity is the per-session exclusion
 * key and what later re-seat work needs; it is never a lookup key for drawing,
 * because substituting one version's occurrence for another's is precisely the
 * migration this release has not built.
 *
 * The detail is bounded and requested only for chairs actually on screen. A
 * whole room's worth used to arrive on every page load — 39.98 MiB at the
 * contract's ceiling — which was correct and unusable.
 */
/**
 * What the appearance builders need from a chair, and no more.
 *
 * Structural on purpose: the organizer's room read and the customer's carry
 * different fields under different names, and both can supply these two. The
 * alternative was a second copy of this logic per surface, which is how two
 * rooms start disagreeing about what a colour means.
 */
export interface DrawnSeat {
  /** The occurrence the server keys availability on. */
  readonly seatId: string;
  /** The key this chair is drawn under. */
  readonly seatKey: string;
}

/** The same, for a section: the id the aggregate names, and the drawing's key. */
export interface DrawnSection {
  readonly sectionId: string;
  readonly code: string;
}

/**
 * What dressing a chair needs to know about it.
 *
 * The organizer's `SeatDetail` and the customer's `SeatOption` both satisfy
 * this and carry different things besides — a claim state on one, a price on
 * the other. The overlay wants neither: it colours a chair by what is happening
 * to it, and that is the same fact on both surfaces.
 */
export interface SeatState {
  readonly venueMapSeatId: string;
  readonly availability: SeatDetail['availability'];
}

export interface SeatingOverlay {
  /** O(1) by exact occurrence. The only lookup the renderer performs. */
  readonly detailByOccurrence: ReadonlyMap<string, SeatState>;
}

export function indexSeatDetail(seats: readonly SeatState[]): SeatingOverlay {
  return { detailByOccurrence: new Map(seats.map((seat) => [seat.venueMapSeatId, seat])) };
}

/**
 * An empty overlay, for a room whose chairs are not drawn.
 *
 * A real object rather than `null`, so no renderer grows a branch asking
 * whether the overlay exists before every lookup.
 */
export const NO_OVERLAY: SeatingOverlay = { detailByOccurrence: new Map() };

/**
 * Dresses the chairs on screen.
 *
 * The join is the interesting part. The renderer draws a *document*, which
 * knows a chair by its `seatKey`; the projection knows the same chair by its
 * `venueMapSeatId`. Both maps are built once, so drawing stays one lookup per
 * chair rather than a search.
 *
 * The appearance carries a word as well as a class, because a chair whose state
 * is only a colour is one a colour-blind operator cannot read.
 */
export function seatAppearances(
  seats: readonly DrawnSeat[],
  overlay: SeatingOverlay,
  /*
   * Which words. The states are the server's either way — this chooses whose
   * sentences, because an operator watching a room and a person buying a chair
   * need different things from the same fact.
   */
  vocabulary: Readonly<Record<string, { label: string }>> = SEAT_AVAILABILITY_STATES,
): ReadonlyMap<string, OverlayAppearance> {
  const appearances = new Map<string, OverlayAppearance>();
  for (const seat of seats) {
    const detail = overlay.detailByOccurrence.get(seat.seatId);
    if (detail === undefined) continue;
    const state = presentation(vocabulary as Readonly<Record<string, never>>, detail.availability);
    appearances.set(seat.seatKey, {
      modifier: detail.availability.toLowerCase(),
      announce: state.label,
    });
  }
  return appearances;
}

/**
 * Dresses whole sections, for when chair detail is suppressed.
 *
 * Counts come from the server's aggregate, never from counting the chairs that
 * happen to be loaded. That is the whole reason the aggregate exists: at the
 * sizes where level of detail engages, the browser is deliberately not holding
 * enough to count, and a number derived from what it *did* draw would be a
 * confident lie.
 */
export function sectionAppearances(
  sections: readonly DrawnSection[],
  summary: SeatingAvailabilitySummary,
): ReadonlyMap<string, OverlayAppearance> {
  const codeById = new Map(sections.map((section) => [section.sectionId, section.code]));
  const appearances = new Map<string, OverlayAppearance>();

  for (const section of summary.sections) {
    const code = codeById.get(section.venueMapSectionId);
    if (code === undefined) continue;
    const { sellable, available, held, committed } = section.counts;
    const modifier = sellable === 0 ? 'unconfigured' : available === 0 ? 'sold' : 'available';
    appearances.set(code, {
      modifier,
      announce:
        sellable === 0
          ? 'nothing on sale here'
          : `${available.toLocaleString()} free, ${held.toLocaleString()} being bought, ${committed.toLocaleString()} sold, of ${sellable.toLocaleString()} on sale`,
    });
  }
  return appearances;
}

/**
 * Splits a set of occurrences into requests the server will accept.
 *
 * The limit is the contract's, and the batching is deterministic so a redraw of
 * the same chairs produces the same requests. The renderer's own ceiling means
 * a single batch covers every currently supported view — but that ceiling is a
 * client constant and this one is the contract, and relying on the server to
 * truncate would mean rendering a room with chairs quietly missing.
 */
export function batchOccurrences(
  occurrences: readonly string[],
  limit: number,
): readonly (readonly string[])[] {
  const unique = [...new Set(occurrences)].sort();
  const batches: string[][] = [];
  for (let index = 0; index < unique.length; index += limit) {
    batches.push(unique.slice(index, index + limit));
  }
  return batches;
}
